import { Box, Grid, GridItem, Heading, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PersonDto, useListContractTasksForOrganizationQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ListLoadError } from "../../../common/ListLoadError";
import { useSkeletons } from "../../../common/useSkeletons";
import { MultiSelectOption } from "../view-all/filtering/MultiSelectFilter";
import { TasksTable } from "./TasksTable";
import { TaskFilters } from "./filtering/TaskFilters";

export const ContractTasks = () => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const [selectedResponsiblePersons, setSelectedResponsiblePersons] = useState<PersonDto[]>([]);
  const [selectedContracts, setSelectedContracts] = useState<MultiSelectOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<MultiSelectOption[]>([]);

  const {
    currentData: tasksForCurrentFilters,
    isError,
    refetch,
  } = useListContractTasksForOrganizationQuery({
    organizationId: authState.selectedOrg.id,
    orgId: authState.selectedOrg.id,
    statuses: selectedStatuses.map((e) => e.value).join(","),
    personIds: selectedResponsiblePersons.map((p) => p.id).join(","),
    contractIds: selectedContracts.map((e) => e.value).join(","),
  });

  const skeletons = useSkeletons().stackedLines;
  const hasActiveFilters =
    selectedResponsiblePersons.length > 0 || selectedContracts.length > 0 || selectedStatuses.length > 0;
  const resetFilters = () => {
    setSelectedResponsiblePersons([]);
    setSelectedContracts([]);
    setSelectedStatuses([]);
  };

  return (
    <Grid height="full" gridTemplateColumns="minmax(0, 1fr)" gridTemplateRows={"70px 1fr"}>
      <GridItem display="flex" alignItems="center" borderBottom={"1px solid #e1e6ee"}>
        <Heading as="h2" size="lg">
          {t("Contract tasks")}
        </Heading>
      </GridItem>
      <GridItem>
        <Box mt="15px" mb="20px">
          <Text fontSize="medium" color={"#585858"} mb="10px">
            {t("Tasks for all contracts, including contracts shared with you")}
          </Text>
        </Box>
        <Box mb="25px">
          <TaskFilters
            selectedResponsiblePersons={selectedResponsiblePersons}
            onSelectedResponsiblePersonsChange={(e) => setSelectedResponsiblePersons(e)}
            selectedContracts={selectedContracts}
            onSelectedContractsChange={(e) => setSelectedContracts(e)}
            selectedStatuses={selectedStatuses}
            onSelectedStatusesChange={(e) => setSelectedStatuses(e)}
          />
        </Box>
        {isError ? (
          <ListLoadError
            message={t("Could not load contract tasks.")}
            onRetry={() => void refetch()}
            reset={hasActiveFilters ? { label: t("Reset table"), onReset: resetFilters } : undefined}
          />
        ) : tasksForCurrentFilters ? (
          <TasksTable
            tasks={tasksForCurrentFilters.tasks}
            hasActiveFilters={hasActiveFilters}
            onResetFilters={resetFilters}
          />
        ) : (
          skeletons(0, 5)
        )}
      </GridItem>
    </Grid>
  );
};
